import { useMemo, useState } from "react";
import { useAction, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { ContentPanel, StatusPill } from "./shared";
import { DollarSign, Mail, RefreshCw, Search } from "lucide-react";

const fmt = new Intl.NumberFormat("en-AU", {
  currency: "AUD",
  style: "currency",
});

function formatDate(ts?: number): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("en-AU", {
    timeZone: "Australia/Brisbane",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type PaymentRow = {
  id: string;
  projectTitle: string;
  amountCents: number;
  paymentMethod: string;
  status: string;
  createdAt: number;
  paidAt?: number;
};

type EmailRow = {
  id: string;
  projectTitle: string;
  recipient?: string;
  message: string;
  deliveryStatus: string;
  error?: string;
  createdAt: number;
};

type ArtistRow = {
  artistId: string;
  name: string;
  stageName?: string;
  email?: string;
  payments: PaymentRow[];
  emails: EmailRow[];
};

type PostmarkMessage = {
  id: string;
  to: string;
  from: string;
  subject: string;
  status: string;
  receivedAt: string;
};

export default function AdminPaymentsEmails() {
  const data = useQuery(api.queries.getAdminPaymentsAndEmails, {});
  const listPostmark = useAction(api.postmarkHistory.listPostmarkOutbound);

  const [selectedArtistId, setSelectedArtistId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [postmark, setPostmark] = useState<PostmarkMessage[] | null>(null);
  const [postmarkLoading, setPostmarkLoading] = useState(false);
  const [postmarkError, setPostmarkError] = useState<string | null>(null);

  const artists: ArtistRow[] = data?.artists ?? [];

  const filteredArtists = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return artists;
    return artists.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        (a.stageName ?? "").toLowerCase().includes(q) ||
        (a.email ?? "").toLowerCase().includes(q),
    );
  }, [artists, query]);

  const selected = artists.find((a) => a.artistId === selectedArtistId) ?? null;

  async function loadPostmark() {
    setPostmarkLoading(true);
    setPostmarkError(null);
    try {
      const result = await listPostmark({ count: 20, offset: 0 });
      if (!result.success) {
        setPostmarkError(result.error ?? "Unable to load Postmark history");
        setPostmark(null);
      } else {
        setPostmark(result.messages ?? []);
      }
    } catch (e) {
      setPostmarkError(e instanceof Error ? e.message : "Unable to load Postmark history");
      setPostmark(null);
    } finally {
      setPostmarkLoading(false);
    }
  }

  const totalPaid = artists.reduce(
    (sum, a) => sum + a.payments.filter((p) => p.status === "paid").reduce((s, p) => s + p.amountCents, 0),
    0,
  );

  return (
    <div className="space-stack">
      <section className="hero-panel">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Payments &amp; Emails</h1>
          <p>Client payments and sent-email history across all artists.</p>
        </div>
      </section>

      <section className="metric-grid">
        <div className="metric-card">
          <DollarSign className="h-5 w-5" />
          <span>
            <strong>{fmt.format(totalPaid / 100)}</strong>
            <small>Total paid</small>
          </span>
        </div>
        <div className="metric-card">
          <Mail className="h-5 w-5" />
          <span>
            <strong>{artists.reduce((s, a) => s + a.emails.length, 0)}</strong>
            <small>Portal emails logged</small>
          </span>
        </div>
      </section>

      <ContentPanel title="Artists" eyebrow="Select">
        <div className="admin-library-toolbar">
          <div className="admin-library-search">
            <Search className="h-4 w-4" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search artists…"
            />
          </div>
        </div>
        <div className="admin-queue-list">
          {filteredArtists.length === 0 ? (
            <p className="empty-state">No artists found.</p>
          ) : (
            filteredArtists.map((a) => (
              <button
                key={a.artistId}
                type="button"
                className={`admin-queue-row ${selectedArtistId === a.artistId ? "tab-active" : ""}`}
                onClick={() => setSelectedArtistId(a.artistId)}
              >
                <div className="admin-queue-main">
                  <strong>{a.stageName || a.name}</strong>
                  <small>
                    {a.email ?? "No email"} · {a.payments.length} payments · {a.emails.length} emails
                  </small>
                </div>
              </button>
            ))
          )}
        </div>
      </ContentPanel>

      {selected ? (
        <>
          <ContentPanel title={`${selected.stageName || selected.name} — Payments`} eyebrow="Ledger">
            {selected.payments.length === 0 ? (
              <p className="empty-state">No payments recorded.</p>
            ) : (
              <div className="admin-glance-list">
                {selected.payments.map((p) => (
                  <div key={p.id} className="admin-glance-row">
                    <span>
                      <strong>{p.projectTitle}</strong>
                      <small>
                        {p.paymentMethod} · {formatDate(p.paidAt ?? p.createdAt)}
                      </small>
                    </span>
                    <span className="admin-queue-side">
                      <strong>{fmt.format(p.amountCents / 100)}</strong>
                      <StatusPill status={p.status} />
                    </span>
                  </div>
                ))}
              </div>
            )}
          </ContentPanel>

          <ContentPanel title={`${selected.stageName || selected.name} — Emails`} eyebrow="History">
            {selected.emails.length === 0 ? (
              <p className="empty-state">No portal emails logged for this artist.</p>
            ) : (
              <div className="admin-glance-list">
                {selected.emails.map((e) => (
                  <div key={e.id} className="admin-glance-row">
                    <span>
                      <strong>{e.projectTitle}</strong>
                      <small>
                        {e.recipient ?? "—"} · {formatDate(e.createdAt)}
                      </small>
                      <span className="admin-queue-reason">{e.message}</span>
                    </span>
                    <span className="admin-queue-side">
                      <StatusPill status={e.deliveryStatus} />
                    </span>
                  </div>
                ))}
              </div>
            )}
          </ContentPanel>
        </>
      ) : (
        <ContentPanel title="Postmark sent history" eyebrow="Live">
          <div className="action-row">
            <button onClick={() => void loadPostmark()} type="button" disabled={postmarkLoading}>
              <RefreshCw className="h-4 w-4" />
              {postmarkLoading ? "Loading…" : "Load Postmark history"}
            </button>
          </div>
          {postmarkError ? <p className="form-error">{postmarkError}</p> : null}
          {postmark ? (
            <div className="admin-glance-list">
              {postmark.map((m) => (
                <div key={m.id} className="admin-glance-row">
                  <span>
                    <strong>{m.subject}</strong>
                    <small>
                      {m.to} · {m.receivedAt}
                    </small>
                  </span>
                  <span className="admin-queue-side">
                    <StatusPill status={m.status} />
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </ContentPanel>
      )}
    </div>
  );
}
